import { Box, Stack, styled } from '@mui/material';
import { Currency, ScalePrice } from '@synergy/models';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import {
    PoolAccordion,
    PoolAccordionDetails,
    PoolAccordionItem,
    PoolAccordionSummary,
    PoolTypography,
} from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components-next';
import { ReactElement } from 'react';

import { useArticleDetailContext } from '../article-detail';

const StyledStack = styled(Stack)(({ theme }) => ({
    rowGap: theme.spacing(0.5),
    paddingRight: theme.spacing(5),
}));

const SpaceBetweenRow = styled(Box)(() => ({
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
}));

const ScalePricesStack = styled(Stack)(({ theme }) => ({
    ...theme.typography.body2,
    height: 47,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
}));

const StyledPoolAccordion = styled(PoolAccordion)(({ theme }) => ({
    width: '100%',

    [theme.breakpoints.up('sm')]: {
        maxWidth: 279,
    },
}));

const ScalePricesSimple = ({
    scalePrices,
    currency,
    orderUnit,
}: {
    scalePrices: ScalePrice[];
    currency: Currency;
    orderUnit: string;
}): ReactElement => {
    return (
        <Box>
            {scalePrices.map(scalePrice => (
                <ScalePricesStack key={scalePrice.id}>
                    <Box>
                        <FormattedMessage
                            id="app.article.detail.from"
                            values={{
                                quantity: scalePrice.quantity,
                                unit: orderUnit,
                            }}
                        />
                    </Box>
                    <Box>
                        <FormattedNumber value={scalePrice.price} currency={currency} style="currency" />
                    </Box>
                </ScalePricesStack>
            ))}
        </Box>
    );
};

export const ArticleDetailScalePrices = (): ReactElement | null => {
    const { article } = useArticleDetailContext();
    const { contentQuantity, priceQuantity, currency, contentUnit, orderUnit, scalePrices, priceReferenceQuantity } =
        article;

    if (scalePrices.length === 0) {
        return null;
    }

    return (
        <>
            <PoolTypography variant="statusLabel">
                <FormattedMessage id="app.article.detail.scalePrice" />
            </PoolTypography>

            {!contentQuantity || !priceQuantity ? (
                <ScalePricesSimple scalePrices={scalePrices} currency={currency} orderUnit={orderUnit} />
            ) : (
                <StyledPoolAccordion variant="small">
                    {scalePrices.map(scalePrice => (
                        <PoolAccordionItem key={scalePrice.id}>
                            <PoolAccordionSummary>
                                <SpaceBetweenRow>
                                    <Box>
                                        <PoolFormattedMessage
                                            id="app.article.detail.from"
                                            values={{
                                                quantity: scalePrice.quantity,
                                                unit: orderUnit,
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <FormattedNumber
                                            value={scalePrice.price * priceQuantity}
                                            currency={currency}
                                            style="currency"
                                            minimumFractionDigits={2}
                                            maximumFractionDigits={4}
                                        />
                                    </Box>
                                </SpaceBetweenRow>
                            </PoolAccordionSummary>
                            <PoolAccordionDetails>
                                <StyledStack>
                                    <SpaceBetweenRow>
                                        <Box>
                                            <FormattedMessage
                                                id="app.article.detail.per"
                                                values={{
                                                    price: null,
                                                    quantity: null,
                                                    unit: orderUnit,
                                                }}
                                            />
                                        </Box>
                                        <Box>
                                            <FormattedNumber
                                                value={scalePrice.price}
                                                style="currency"
                                                currency={currency}
                                                minimumFractionDigits={2}
                                                maximumFractionDigits={4}
                                            />
                                        </Box>
                                    </SpaceBetweenRow>
                                    <SpaceBetweenRow>
                                        <Box>
                                            <FormattedMessage
                                                id="app.article.detail.per"
                                                values={{
                                                    price: null,
                                                    quantity: null,
                                                    unit: contentUnit,
                                                }}
                                            />
                                        </Box>
                                        <Box>
                                            <FormattedNumber
                                                value={scalePrice.price / (contentQuantity || 1)}
                                                style="currency"
                                                currency={currency}
                                                minimumFractionDigits={2}
                                                maximumFractionDigits={4}
                                            />
                                        </Box>
                                    </SpaceBetweenRow>
                                    <SpaceBetweenRow>
                                        <Box>
                                            <FormattedMessage
                                                id="app.article.detail.per"
                                                values={{
                                                    price: null,
                                                    quantity: priceReferenceQuantity,
                                                    unit: contentUnit,
                                                }}
                                            />
                                        </Box>
                                        <Box>
                                            <FormattedNumber
                                                value={
                                                    (scalePrice.price / (contentQuantity || 1)) *
                                                    (priceReferenceQuantity || 1)
                                                }
                                                style="currency"
                                                currency={currency}
                                                minimumFractionDigits={2}
                                                maximumFractionDigits={4}
                                            />
                                        </Box>
                                    </SpaceBetweenRow>
                                </StyledStack>
                            </PoolAccordionDetails>
                        </PoolAccordionItem>
                    ))}
                </StyledPoolAccordion>
            )}
        </>
    );
};
